import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function MoneyAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <div ref={ref} className="relative w-full h-72 md:h-80">
      <svg viewBox="0 0 400 220" className="w-full h-full">
        {/* Wallet - isometric */}
        <motion.g
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.5, type: 'spring' }}
        >
          {/* Wallet body */}
          <rect x="30" y="70" width="100" height="70" rx="8" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="2" />
          <rect x="35" y="75" width="90" height="30" rx="4" fill="#a78bfa" />
          <text x="80" y="95" textAnchor="middle" fontSize="12" fill="white" fontWeight="bold">
            💰 £500
          </text>
          <text x="80" y="125" textAnchor="middle" fontSize="10" fill="#e9d5ff">
            Your Wallet
          </text>
        </motion.g>

        {/* Arrow 1: Hold */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.8 }}
        >
          <motion.path
            d="M 135 105 C 160 105, 160 60, 200 60"
            stroke="#f59e0b"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          />
          <motion.polygon
            points="205,60 195,55 195,65"
            fill="#f59e0b"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: 1.3 }}
          />
          <motion.text
            x="165"
            y="50"
            fontSize="10"
            fill="#d97706"
            fontWeight="500"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: 1 }}
          >
            You bid £100
          </motion.text>
        </motion.g>

        {/* Hold box */}
        <motion.g
          initial={{ scale: 0, opacity: 0 }}
          animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
          transition={{ delay: 1.4, type: 'spring' }}
        >
          <rect x="210" y="40" width="80" height="45" rx="6" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4 2" />
          <text x="250" y="58" textAnchor="middle" fontSize="9" fill="#92400e" fontWeight="500">
            🔒 ON HOLD
          </text>
          <text x="250" y="75" textAnchor="middle" fontSize="14" fill="#d97706" fontWeight="bold">
            £100
          </text>
        </motion.g>

        {/* Split paths */}
        {/* Path A: You Win! */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 2 }}
        >
          <motion.path
            d="M 295 55 C 320 55, 340 55, 340 90"
            stroke="#22c55e"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 0.5, delay: 2 }}
          />
          <motion.polygon
            points="340,95 335,85 345,85"
            fill="#22c55e"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: 2.4 }}
          />
        </motion.g>

        {/* Win outcome */}
        <motion.g
          initial={{ scale: 0, opacity: 0 }}
          animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
          transition={{ delay: 2.5, type: 'spring' }}
        >
          <rect x="300" y="100" width="90" height="50" rx="6" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" />
          <text x="345" y="120" textAnchor="middle" fontSize="10" fill="#166534" fontWeight="bold">
            🎉 YOU WIN!
          </text>
          <text x="345" y="138" textAnchor="middle" fontSize="9" fill="#15803d">
            Payment captured
          </text>
        </motion.g>

        {/* Path B: Outbid */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 2 }}
        >
          <motion.path
            d="M 295 70 C 320 70, 330 100, 330 130 C 330 160, 280 180, 180 180"
            stroke="#6b7280"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeDasharray="4 2"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 0.8, delay: 2.8 }}
          />
          <motion.polygon
            points="175,180 185,175 185,185"
            fill="#6b7280"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: 3.5 }}
          />
        </motion.g>

        {/* Outbid outcome - money returns */}
        <motion.g
          initial={{ opacity: 0, y: -10 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
          transition={{ delay: 3.6 }}
        >
          <rect x="55" y="155" width="80" height="35" rx="6" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
          <text x="95" y="172" textAnchor="middle" fontSize="9" fill="#1e40af" fontWeight="500">
            💸 Outbid?
          </text>
          <text x="95" y="184" textAnchor="middle" fontSize="9" fill="#1d4ed8">
            Money returns!
          </text>
        </motion.g>

        {/* Labels */}
        <motion.text
          x="315"
          y="165"
          fontSize="9"
          fill="#9ca3af"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 3 }}
        >
          or outbid...
        </motion.text>
      </svg>
    </div>
  );
}
